/**
 * @module nums
 *
 * Number manipulations, conversions, transformations, and other math.
 */

/**
 * Take `value` that is a fraction (e.g., 0.23455332) and return a floating
 * point number representation, multiplied by 100, rounded to `digits` decimals.
 */

const fixedPercent = (value: number, digits = 1) => {
  // @ts-ignore @TODO Epic WT-21600
  const percent = Number.parseFloat(value) * 100;
  const fixed = percent.toFixed(digits);

  return fixed;
};

/**
 * Returns a string representation of the `fixedPercent()` which includes an
 * explicity +/- sign (or no sign if the value is zero).
 */
const signedPercent = (value: number, digits = 1): string => {
  const percent = fixedPercent(value, digits);

  // If positive sign, explicitly return "+" with value, otherwise just return
  // the string of the value (negative numbers already include their sign).
  return Math.sign(value) > 0 ? `+${percent}` : `${percent}`;
};

const MEANINGFUL_POSITIVE = 0.0005;
const MEANINGFUL_NEGATIVE = -0.0005;
/**
 * Receive float value, return true if it is meaningful positive percentage.
 */
const isPositivePercent = (value: number): boolean => value >= MEANINGFUL_POSITIVE;

/**
 * Receive float value, return true if it is meaningful negative percentage.
 */
const isNegativePercent = (value: number): boolean => value <= MEANINGFUL_NEGATIVE;

/**
 * Receive float value, return true if it should be treated as 0 for percentage.
 */
const isZeroPercent = (value: number): boolean => value < MEANINGFUL_POSITIVE && value > MEANINGFUL_NEGATIVE;

/**
 * Return true if `input` is not a number.
 *
 * The following works because NaN is the only value in JavaScript which is not
 * equal to itself.
 *
 * ref: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isNaN
 */
const isNumberNaN = (input: any): boolean => {
  return typeof input === 'number' && input !== input;
};

/**
 * convert a number to the equivalent spreadsheet column letter
 * eg: 1 -> A, 2 -> B
 * ref: https://stackoverflow.com/questions/45787459/convert-number-to-alphabet-string-javascript/45787487
 */
const numToAlphabet = (num: number): string | null => {
  let s = '';
  let t;

  while (num > 0) {
    t = (num - 1) % 26;
    s = String.fromCharCode(65 + t) + s;
    num = ((num - t) / 26) | 0;
  }
  return s || null;
};

/**
 * Return true if val is numeric (e.g., could be '123' or 123).
 */
const isNumeric = (val: any): boolean => {
  return /^-?\d+$/.test(val);
};

/*
 * Clamp gets a number in between the min and max values, including the min and max values.
 */
const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/*
 * Shortens a number for tight spaces ("1.2K", "60K", "1.2M") in the locale's format. It truncates rather than
 * rounds, so the shortened value never overstates the real one: 59,999 is "59.9K", not "60K".
 */
const compactNumber = (value: number, locale?: string): string =>
  new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1, roundingMode: 'trunc' }).format(value);

const fullCount = (value: number, locale?: string): string => new Intl.NumberFormat(locale).format(value);

export const nums = {
  fixedPercent,
  signedPercent,
  isPositivePercent,
  isNegativePercent,
  isZeroPercent,
  isNumberNaN,
  isNumeric,
  numToAlphabet,
  clamp,
  compactNumber,
  fullCount,
};
